import { SiteShell } from "@/components/site/SiteShell";
import { PageHero } from "@/components/site/PageHero";
import { SEO, breadcrumbSchema } from "@/components/site/SEO";
import { Link } from "react-router-dom";
import { SITE } from "@/lib/siteData";
import { ROUTE_SEO } from "@/lib/routeSeo";

const LAST_UPDATED = "5 June 2026";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 space-y-3">
    <h2 className="font-display uppercase text-foreground text-2xl sm:text-3xl tracking-[0.02em]">{title}</h2>
    <div className="text-sm sm:text-base text-foreground/85 leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <SiteShell>
      <SEO
        title={ROUTE_SEO["/privacy"].title}
        description={ROUTE_SEO["/privacy"].meta}
        path="/privacy"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Privacy Policy" }])}
      />
      <PageHero
        kicker="Legal"
        title={ROUTE_SEO["/privacy"].h1}
        subtitle={`How we collect, use and protect your personal information. Last updated ${LAST_UPDATED}.`}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Privacy Policy" }]}
      />

      <section className="section-pad">
        <div className="container-site grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3">
            <div className="sticky top-24 rounded-2xl border border-border bg-card p-5">
              <p className="kicker mb-3">On this page</p>
              <ul className="space-y-2 text-sm">
                {[
                  ["who", "Who we are"],
                  ["data", "Information we collect"],
                  ["use", "How we use information"],
                  ["share", "Sharing & disclosure"],
                  ["cookies", "Cookies & tracking"],
                  ["retention", "Retention"],
                  ["rights", "Your rights"],
                  ["security", "Security"],
                  ["intl", "International transfers"],
                  ["children", "Children's privacy"],
                  ["changes", "Changes to this policy"],
                  ["contact", "Contact us"],
                ].map(([id, label]) => (
                  <li key={id}><a href={`#${id}`} className="text-muted-foreground hover:text-foreground transition-colors">{label}</a></li>
                ))}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-9 space-y-12">
            <Section id="who" title="Who we are">
              <p>Pie in the Sky Productions ("we", "us", "our") is a Cape Town based video production company. This Privacy Policy explains how we collect, use, share and protect personal information of visitors, clients and prospective clients of {SITE.website} and any related services we provide.</p>
              <p>We comply with the Protection of Personal Information Act, 4 of 2013 (POPIA) of South Africa and, where applicable, the EU/UK General Data Protection Regulation (GDPR).</p>
            </Section>

            <Section id="data" title="Information we collect">
              <p>We collect the following categories of personal information:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong>Contact information</strong> you provide via our forms - name, email address, phone number, company name.</li>
                <li><strong>Project brief information</strong> - the content of any project enquiry, budget indication and dates you submit.</li>
                <li><strong>Booking information</strong> - data captured by our scheduling provider (Calendly) when you book a call, including name, email and chosen time.</li>
                <li><strong>Technical & usage information</strong> - IP address, browser type, device information, pages visited, referrer URL and timestamps, collected via cookies and analytics tools.</li>
                <li><strong>Production materials</strong> - any briefs, scripts, footage, photographs, contracts and feedback you share with us in the course of a production.</li>
                <li><strong>Marketing preferences</strong> - whether you have opted in to receive newsletters or marketing emails.</li>
              </ul>
            </Section>

            <Section id="use" title="How we use information">
              <p>We use personal information for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Responding to enquiries and providing quotations.</li>
                <li>Delivering production, post-production and creative services we have agreed to provide.</li>
                <li>Issuing invoices, processing payments and complying with tax and accounting obligations.</li>
                <li>Managing relationships with clients, talent, crew and suppliers.</li>
                <li>Operating, maintaining and improving the {SITE.website} website.</li>
                <li>Sending service-related communications such as updates, scheduling confirmations or contract notices.</li>
                <li>With your consent, sending marketing communications about our services, blog posts and case studies.</li>
                <li>Complying with legal and regulatory obligations.</li>
              </ul>
              <p>Our lawful bases for processing include performance of a contract with you, your consent, our legitimate interests (running and growing our business) and compliance with legal obligations.</p>
            </Section>

            <Section id="share" title="Sharing & disclosure">
              <p>We do not sell personal information. We share information only with:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong>Service providers</strong> we use to operate our business - including hosting providers, email service providers (e.g. Brevo), scheduling providers (Calendly), analytics providers (e.g. PostHog) and accounting providers. These providers process information on our behalf under written agreements.</li>
                <li><strong>Production partners</strong> - crew, freelance talent, post-production specialists and location partners - strictly to the extent necessary to deliver a production we have agreed to provide.</li>
                <li><strong>Professional advisors</strong> - lawyers, accountants and insurers - where reasonably required.</li>
                <li><strong>Authorities</strong> - where required by law, court order or other legal process.</li>
              </ul>
            </Section>

            <Section id="cookies" title="Cookies & tracking">
              <p>Our website uses cookies and similar tracking technologies to operate the site, remember preferences and understand how visitors use it. Examples include:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong>Essential cookies</strong> - required for the site to function.</li>
                <li><strong>Analytics cookies</strong> (PostHog and similar) - help us understand site usage so we can improve content and performance.</li>
                <li><strong>Embedded media</strong> - YouTube and Calendly embeds may set their own cookies. We use privacy-enhanced (nocookie) embeds where available.</li>
              </ul>
              <p>You can control or disable cookies via your browser settings. Disabling cookies may affect site functionality.</p>
            </Section>

            <Section id="retention" title="Retention">
              <p>We keep personal information only for as long as is necessary to fulfil the purposes set out in this Policy, comply with legal and accounting obligations, resolve disputes and enforce our agreements. Client and supplier records are typically retained for at least seven years to comply with South African tax legislation.</p>
            </Section>

            <Section id="rights" title="Your rights">
              <p>Under POPIA (and GDPR where applicable) you have the right to:</p>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Ask whether we hold personal information about you and request a copy.</li>
                <li>Request that we correct or update inaccurate information.</li>
                <li>Request that we delete information we no longer need or have no lawful basis to keep.</li>
                <li>Object to processing for direct marketing and unsubscribe from marketing emails at any time.</li>
                <li>Withdraw consent where processing is based on your consent.</li>
                <li>Lodge a complaint with the Information Regulator of South Africa.</li>
              </ul>
              <p>To exercise any of these rights, please email us at <a className="underline underline-offset-4 hover:text-[hsl(var(--primary))]" href={`mailto:${SITE.email}`}>{SITE.email}</a>.</p>
            </Section>

            <Section id="security" title="Security">
              <p>We use reasonable technical and organisational measures to protect personal information against loss, misuse or unauthorised access. These include encrypted connections to our website, restricted internal access to client data and secure file storage with reputable cloud providers. No system is 100% secure, and we cannot guarantee absolute security.</p>
            </Section>

            <Section id="intl" title="International transfers">
              <p>Some of our service providers are based outside South Africa (for example in the United States or European Union). Where personal information is transferred internationally, we rely on contractual safeguards and the recipient's adherence to data-protection standards substantially similar to those of POPIA.</p>
            </Section>

            <Section id="children" title="Children's privacy">
              <p>Our services are not directed to children under 18. We do not knowingly collect personal information from minors without parental consent. If you believe a minor has provided us with personal information, please contact us so we can delete it.</p>
            </Section>

            <Section id="changes" title="Changes to this policy">
              <p>We may update this Privacy Policy from time to time. Material changes will be reflected on this page with an updated "Last updated" date. We encourage you to review this Policy periodically.</p>
            </Section>

            <Section id="contact" title="Contact us">
              <p>For privacy questions or requests, please contact:</p>
              <p>
                Pie in the Sky Productions<br />
                {SITE.location}<br />
                Email: <a className="underline underline-offset-4 hover:text-[hsl(var(--primary))]" href={`mailto:${SITE.email}`}>{SITE.email}</a><br />
                Phone: <a className="underline underline-offset-4 hover:text-[hsl(var(--primary))]" href={`tel:${SITE.phone.replace(/\s+/g, "")}`}>{SITE.phone}</a>
              </p>
              <p className="pt-4">See also: <Link to="/working-agreement" className="underline underline-offset-4 hover:text-[hsl(var(--primary))]">Working Agreement & Terms</Link>.</p>
            </Section>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
